import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Chip,
  Box,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Button,
  Avatar,
  LinearProgress,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  InfoOutlined,
  TrendingDown,
  Person,
  Groups,
  Assessment,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { LowPerformanceDialog } from '../Dialogs/LowPerformanceDialog';

// Performance status chip component
const PerformanceStatusChip = ({ status }) => {
  const configs = {
    'High Concern': {
      icon: <ErrorIcon fontSize="small" />,
      color: 'error',
      bgcolor: 'error.lighter',
    },
    'Moderate Concern': {
      icon: <WarningIcon fontSize="small" />,
      color: 'warning',
      bgcolor: 'warning.lighter',
    },
    default: {
      icon: <CheckCircleIcon fontSize="small" />,
      color: 'success',
      bgcolor: 'success.lighter',
    },
  };

  const config = configs[status] || configs.default;

  return (
    <Chip
      icon={config.icon}
      label={status}
      size="small"
      sx={{
        bgcolor: config.bgcolor,
        color: `${config.color}.dark`,
        fontWeight: 600,
        fontSize: 11,
      }}
    />
  );
};

// Compact tutor card component
const TutorCard = ({ tutor, onViewDetails }) => {
  const getScoreColor = (score) => {
    if (score >= 80) return 'success.main';
    if (score >= 60) return 'warning.main';
    return 'error.main';
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 1.5,
        transition: 'all 0.2s',
        '&:hover': {
          boxShadow: 2,
          borderColor: 'primary.main',
        },
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack spacing={1.5}>
          {/* Header Row */}
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1.5} alignItems="center" flex={1}>
              <Avatar
                sx={{
                  bgcolor: 'primary.main',
                  width: 36,
                  height: 36,
                  fontSize: 14,
                  fontWeight: 700,
                }}
              >
                {getInitials(tutor.fullname)}
              </Avatar>
              <Box flex={1}>
                <Typography variant="subtitle2" fontWeight={700} noWrap>
                  {tutor.fullname}
                </Typography>
                <PerformanceStatusChip status={tutor.performance_status} />
              </Box>
            </Stack>
            <Tooltip title="View Details">
              <IconButton size="small" onClick={() => onViewDetails(tutor)}>
                <InfoOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          {/* Metrics Grid */}
          <Stack direction="row" spacing={2} sx={{ pt: 0.5 }}>
            {/* Sessions */}
            <Box flex={1}>
              <Stack direction="row" spacing={0.5} alignItems="center" mb={0.5}>
                <Assessment sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Sessions
                </Typography>
              </Stack>
              <Typography
                variant="h6"
                fontWeight={700}
                color={tutor.session_count < 5 ? 'error.main' : 'text.primary'}
              >
                {tutor.session_count}
              </Typography>
              {tutor.session_count < 5 && (
                <Typography variant="caption" color="error.main">
                  Below target
                </Typography>
              )}
            </Box>

            {/* Students */}
            <Box flex={1}>
              <Stack direction="row" spacing={0.5} alignItems="center" mb={0.5}>
                <Groups sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Students
                </Typography>
              </Stack>
              <Typography
                variant="h6"
                fontWeight={700}
                color={tutor.unique_student_count < 10 ? 'warning.main' : 'text.primary'}
              >
                {tutor.unique_student_count}
              </Typography>
              {tutor.unique_student_count < 10 && (
                <Typography variant="caption" color="warning.main">
                  Low reach
                </Typography>
              )}
            </Box>

            {/* Average Score */}
            <Box flex={1}>
              <Stack direction="row" spacing={0.5} alignItems="center" mb={0.5}>
                <TrendingDown sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Avg Score
                </Typography>
              </Stack>
              <Typography
                variant="h6"
                fontWeight={700}
                color={tutor.avg_student_score ? getScoreColor(tutor.avg_student_score) : 'text.secondary'}
              >
                {tutor.avg_student_score ? `${Math.round(tutor.avg_student_score)}%` : 'N/A'}
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

// Main component
const TutorLowPerformance = ({ data = [] }) => {
  const [selectedTutor, setSelectedTutor] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [showAll, setShowAll] = useState(false);

  const safeData = Array.isArray(data) ? data : [];
  const isEmpty = safeData.length === 0;

  const handleViewDetails = (tutor) => {
    setSelectedTutor(tutor);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedTutor(null);
  };

  // Categorize tutors by severity
  const categorizedData = {
    highConcern: safeData.filter((t) => t.performance_status === 'High Concern') || [],
    moderateConcern: safeData.filter((t) => t.performance_status === 'Moderate Concern') || [],
    onTrack: safeData.filter((t) => t.performance_status === 'On Track') || [],
  };

  const displayLimit = showAll ? data.length : 5;
  const displayData = safeData.slice(0, displayLimit);

  // Empty state
  if (isEmpty) {
    return (
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: 2,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.paper',
        }}
      >
        <Stack spacing={2} alignItems="center" textAlign="center">
          <Box
            sx={{
              borderRadius: '50%',
              bgcolor: 'success.lighter',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 32, color: 'success.main' }} />
          </Box>
          <Stack spacing={0.5}>
            <Typography variant="h6" fontWeight={700}>
              All Tutors Performing Well
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No tutors requiring immediate attention
            </Typography>
          </Stack>
        </Stack>
      </Paper>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Box flex={1}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Low Performing Tutors
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Tutors with low session counts or student engagement
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setExpanded(!expanded)}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Stack>

        {/* Summary Stats */}
        {expanded && (
          <Stack direction="row" spacing={1} mt={2}>
            <Chip
              label={`${categorizedData.highConcern.length} High Concern`}
              size="small"
              color="error"
              variant="outlined"
            />
            <Chip
              label={`${categorizedData.moderateConcern.length} Moderate`}
              size="small"
              color="warning"
              variant="outlined"
            />
            <Chip
              label={`${data.length} Total`}
              size="small"
              variant="outlined"
            />
          </Stack>
        )}

        <Divider sx={{ mt: 2 }} />
      </Box>

      {/* Collapsible Content */}
      {expanded && (
        <>
          {/* Scrollable List */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              pr: 1,
              maxHeight: 500,
              '&::-webkit-scrollbar': {
                width: 6,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'divider',
                borderRadius: 3,
              },
            }}
          >
            {displayData.map((tutor) => (
              <TutorCard key={tutor.id} tutor={tutor} onViewDetails={handleViewDetails} />
            ))}
          </Box>

          {/* Show More/Less Button */}
          {data.length > 5 && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button
                size="small"
                variant="text"
                onClick={() => setShowAll(!showAll)}
                endIcon={showAll ? <ExpandLess /> : <ExpandMore />}
              >
                {showAll ? 'Show Less' : `Show ${data.length - 5} More`}
              </Button>
            </Box>
          )}
        </>
      )}

      {/* Dialog */}
      <LowPerformanceDialog open={dialogOpen} onClose={handleCloseDialog} tutor={selectedTutor} />
    </Box>
  );
};

export default TutorLowPerformance;